import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { 
  Building2, CreditCard, AlertCircle, CheckCircle2, 
  RefreshCw, Loader2, TrendingUp, TrendingDown, Clock, Ban,
  Plus, Unlink, ExternalLink, Wallet, Trash2, AlertTriangle,
  ShieldAlert, WifiOff, KeyRound, XCircle
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Statuts Bridge et leur signification
const BRIDGE_STATUS = {
  0: { label: "Connecté", color: "green", icon: CheckCircle2, needsAction: false },
  402: { label: "Abonnement requis", color: "orange", icon: AlertTriangle, needsAction: true },
  429: { label: "Trop de tentatives", color: "orange", icon: Clock, needsAction: false },
  430: { label: "Identifiants incorrects", color: "red", icon: XCircle, needsAction: true },
  1010: { label: "Authentification requise", color: "red", icon: KeyRound, needsAction: true },
  1100: { label: "Site bancaire indisponible", color: "orange", icon: WifiOff, needsAction: false },
  1200: { label: "Identifiants incorrects", color: "red", icon: XCircle, needsAction: true },
  OTHER: { label: "Déconnecté", color: "red", icon: AlertCircle, needsAction: true }
};

export default function Dashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [items, setItems] = useState([]);  // Items Bridge (connexions bancaires)
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);
  const [error, setError] = useState(null);

  // Filtrer les comptes actifs (non déconnectés)
  const activeAccounts = useMemo(() => 
    accounts.filter(acc => acc.status !== 'disconnected'),
    [accounts]
  );

  // Identifier les connexions qui nécessitent une action
  const itemsNeedingAction = useMemo(() => 
    items.filter(item => {
      const statusInfo = BRIDGE_STATUS[item.status] || BRIDGE_STATUS.OTHER;
      return statusInfo.needsAction;
    }),
    [items]
  );

  // Grouper les comptes par banque (bridge_item_id)
  const accountsByBank = useMemo(() => {
    const grouped = {};
    activeAccounts.forEach(acc => {
      const itemId = acc.bridge_item_id || 'default';
      // Trouver l'item correspondant pour son statut et nom de banque
      const item = items.find(i => i.id === parseInt(itemId)) || {};
      
      if (!grouped[itemId]) {
        grouped[itemId] = {
          itemId,
          // Priorité: item.bank_name (from Bridge API) > acc.provider_name > default
          bankName: item.bank_name || acc.provider_name || 'Banque',
          bankLogo: item.bank_logo_url || acc.provider_logo_url,
          status: item.status || 0,
          statusInfo: item.status_code_info,
          accounts: []
        };
      }
      grouped[itemId].accounts.push(acc);
    });
    return Object.values(grouped);
  }, [activeAccounts, items]);

  // Calculer le solde total de tous les comptes
  const totalBalance = useMemo(() => 
    activeAccounts.reduce((sum, acc) => sum + (acc.balance || 0), 0),
    [activeAccounts]
  );

  // Multi-banques = plus d'une banque connectée
  const isMultiBank = accountsByBank.length > 1 || activeAccounts.length > 2;

  const fetchData = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setLoading(true);
      const [statsRes, accountsRes, itemsRes] = await Promise.all([
        axios.get(`${API}/api/bridge-v2/rapprochement/stats`, { headers }),
        axios.get(`${API}/api/bridge-v2/accounts`, { headers }),
        axios.get(`${API}/api/bridge-v2/items`, { headers }).catch(() => ({ data: { items: [] } }))
      ]);
      setStats(statsRes.data);
      setAccounts(accountsRes.data.accounts || []);
      setItems(itemsRes.data.items || []);
      setError(null);
    } catch (err) {
      console.error("Erreur chargement données:", err);
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setSyncing(true);
      await axios.post(`${API}/api/bridge-v2/sync`, {}, { headers });
      await fetchData();
    } catch (err) {
      console.error("Erreur sync:", err);
      setError("Erreur lors de la synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  // Connecter une nouvelle banque (crée un nouvel Item Bridge sur le même User)
  const handleConnectBank = async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setConnecting(true);
      const response = await axios.post(`${API}/api/bridge-v2/connect`, {
        callback_url: `${window.location.origin}/rapprochement-v2`
      }, { headers });
      
      if (response.data.connect_url) {
        // Ouvrir Bridge Connect dans une nouvelle fenêtre
        window.open(response.data.connect_url, '_blank', 'width=600,height=700');
      }
    } catch (err) {
      console.error("Erreur connexion banque:", err);
      setError("Erreur lors de la connexion à Bridge");
    } finally {
      setConnecting(false);
    }
  };

  // Déconnecter un compte (le supprime de l'affichage)
  const handleDisconnectAccount = async (accountId) => {
    if (!token || !window.confirm("Êtes-vous sûr de vouloir déconnecter ce compte ? Il disparaîtra du tableau de bord.")) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setDisconnecting(accountId);
      await axios.post(`${API}/api/bridge-v2/accounts/${accountId}/disconnect`, {}, { headers });
      await fetchData();
    } catch (err) {
      console.error("Erreur déconnexion:", err);
      setError("Erreur lors de la déconnexion du compte");
    } finally {
      setDisconnecting(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Paris'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const pendingCount = stats?.en_attente || 0;
  const reconciledCount = stats?.rapprochees || 0;
  const totalCount = stats?.total_transactions || 0;
  const reconciledPercent = totalCount > 0 ? Math.round((reconciledCount / totalCount) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="rapprochement-dashboard">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Rapprochement Bancaire</h1>
          <p className="text-slate-500 mt-1">Synchronisation automatique avec Bridge API</p>
        </div>
        <Button 
          onClick={handleSync} 
          disabled={syncing}
          data-testid="sync-btn"
        >
          {syncing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Synchroniser
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* BANNIÈRE D'ALERTE - CONNEXIONS DÉCONNECTÉES (style Pennylane) */}
      {itemsNeedingAction.length > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 rounded-lg p-4 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <ShieldAlert className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                {itemsNeedingAction.length === 1 
                  ? "Une connexion bancaire nécessite votre attention"
                  : `${itemsNeedingAction.length} connexions bancaires nécessitent votre attention`
                }
              </h3>
              <p className="text-sm text-red-700 mt-1">
                Pour continuer à recevoir vos transactions automatiquement, veuillez renouveler votre authentification.
              </p>
              
              <div className="mt-3 space-y-2">
                {itemsNeedingAction.map(item => {
                  const statusInfo = BRIDGE_STATUS[item.status] || BRIDGE_STATUS.OTHER;
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <div 
                      key={item.id}
                      className="flex items-center justify-between bg-white rounded-lg p-3 border border-red-200"
                    >
                      <div className="flex items-center gap-3">
                        <StatusIcon className={`w-5 h-5 text-${statusInfo.color}-600`} />
                        <div>
                          <p className="font-medium text-slate-800">{item.bank_name || 'Banque'}</p>
                          <p className="text-xs text-slate-500">
                            {statusInfo.label}
                            {item.status_code_info && ` - ${item.status_code_info}`}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                        onClick={handleConnectBank}
                        disabled={connecting}
                      >
                        {connecting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <KeyRound className="w-4 h-4 mr-2" />
                            Reconnecter
                          </>
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerte si AUCUN compte connecté */}
      {activeAccounts.length === 0 && !loading && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-500 rounded-lg p-4 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <WifiOff className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-800">
                Aucun compte bancaire connecté
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Connectez votre banque pour importer automatiquement vos transactions et activer le rapprochement bancaire.
              </p>
              <Button
                className="mt-3 bg-amber-600 hover:bg-amber-700"
                onClick={handleConnectBank}
                disabled={connecting}
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Connecter ma banque
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="stat-pending">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">À valider</p>
                <p className="text-3xl font-bold text-orange-600">{pendingCount}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-reconciled">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Rapprochées</p>
                <p className="text-3xl font-bold text-green-600">{reconciledCount}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-total">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total transactions</p>
                <p className="text-3xl font-bold text-slate-900">{totalCount}</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-full">
                <CreditCard className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-progress">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-slate-500">Progression</p>
              <span className="text-lg font-bold text-blue-600">{reconciledPercent}%</span>
            </div>
            <Progress value={reconciledPercent} className="h-2" />
            <p className="text-xs text-slate-400 mt-2">
              {reconciledCount} / {totalCount} transactions traitées
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Comptes bancaires connectés */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Comptes bancaires connectés
          </CardTitle>
          <Button 
            onClick={handleConnectBank}
            disabled={connecting}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
          >
            {connecting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Connecter une banque
          </Button>
        </CardHeader>
        <CardContent>
          {activeAccounts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucun compte bancaire connecté</p>
              <p className="text-sm mt-1">Connectez votre banque via Bridge pour commencer</p>
              <Button 
                onClick={handleConnectBank}
                disabled={connecting}
                className="mt-4 bg-blue-600 hover:bg-blue-700"
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                Connecter ma banque
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* KPI Solde Total si multi-banques */}
              {isMultiBank && (
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 font-medium">Solde Total</p>
                      <p className="text-xs text-slate-400">{activeAccounts.length} comptes connectés</p>
                    </div>
                  </div>
                  <p className={`text-2xl font-bold ${totalBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(totalBalance)}
                  </p>
                </div>
              )}
              
              {/* Comptes groupés par banque */}
              {accountsByBank.map((bank) => {
                const statusInfo = BRIDGE_STATUS[bank.status] || BRIDGE_STATUS[0];
                const StatusIcon = statusInfo.icon;
                const isConnected = bank.status === 0 || bank.status === undefined;
                
                return (
                <div key={bank.itemId} className={`border rounded-lg overflow-hidden ${!isConnected ? 'border-red-300 bg-red-50/30' : ''}`}>
                  {/* Header de la banque avec statut */}
                  <div className={`flex items-center justify-between px-4 py-2 border-b ${
                    !isConnected ? 'bg-red-100' : 'bg-slate-100'
                  }`}>
                    <div className="flex items-center gap-3">
                      {bank.bankLogo ? (
                        <img 
                          src={bank.bankLogo} 
                          alt={bank.bankName}
                          className="w-6 h-6 rounded object-contain bg-white"
                        />
                      ) : (
                        <Building2 className="w-5 h-5 text-slate-500" />
                      )}
                      <span className="font-medium text-slate-700">{bank.bankName}</span>
                      <Badge className="bg-slate-200 text-slate-600 text-[10px]">
                        {bank.accounts.length} compte{bank.accounts.length > 1 ? 's' : ''}
                      </Badge>
                    </div>
                    
                    {/* Badge de statut */}
                    <div className="flex items-center gap-2">
                      {isConnected ? (
                        <Badge className="bg-green-100 text-green-700 text-xs flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Connecté
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 text-xs flex items-center gap-1 animate-pulse">
                          <StatusIcon className="w-3 h-3" />
                          {statusInfo.label}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Alerte reconnexion si nécessaire */}
                  {!isConnected && (
                    <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between">
                      <p className="text-sm text-red-700">
                        <AlertTriangle className="w-4 h-4 inline mr-1" />
                        La synchronisation est interrompue. Reconnectez-vous pour continuer à recevoir vos transactions.
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-100"
                        onClick={handleConnectBank}
                        disabled={connecting}
                      >
                        <KeyRound className="w-3 h-3 mr-1" />
                        Reconnecter
                      </Button>
                    </div>
                  )}
                  
                  {/* Liste des comptes de cette banque */}
                  <div className="divide-y">
                    {bank.accounts.map((account) => (
                      <div 
                        key={account.id || account.bridge_account_id}
                        className="flex items-center justify-between p-3 hover:bg-slate-50"
                        data-testid={`account-${account.bridge_account_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            account.name?.toLowerCase().includes('carte') 
                              ? 'bg-orange-100' 
                              : 'bg-blue-100'
                          }`}>
                            {account.name?.toLowerCase().includes('carte') ? (
                              <CreditCard className="w-4 h-4 text-orange-600" />
                            ) : (
                              <Building2 className="w-4 h-4 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm text-slate-900">{account.name}</p>
                            <p className="text-xs text-slate-400">{account.iban || 'IBAN non disponible'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className={`font-bold ${account.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(account.balance)}
                            </p>
                            <p className="text-[10px] text-slate-400">
                              {formatDate(account.last_sync_at)}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            onClick={() => handleDisconnectAccount(account.bridge_account_id)}
                            disabled={disconnecting === account.bridge_account_id}
                            title="Déconnecter ce compte"
                          >
                            {disconnecting === account.bridge_account_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )})}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Répartition par statut */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">En attente validation</p>
                  <p className="text-xl font-bold">{stats.awaiting_validation || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Classées "Divers"</p>
                  <p className="text-xl font-bold">{stats.divers_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Ban className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Ignorées</p>
                  <p className="text-xl font-bold">{stats.ignored_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
